/**
 * Who a {@link FormShell} is being rendered for, and how much of that the leaf
 * renderers are allowed to know.
 *
 * <h3>Why there are two types here</h3>
 * `mode` is the shell's oldest discriminator and it is read in ~16 places in
 * `FormShell` plus a dozen more across `FormRenderer`, `FieldRenderer`,
 * `RepeatableRowControl` and `OneSpanESignStep`. Several of those are written
 * `mode === 'preview' || ...` or `mode !== 'advisor'` — which means a third
 * member added to one union silently lands on whichever branch the author
 * never considered. Two sites would have been wrong by default in OPPOSITE
 * directions: `FormRenderer`'s section visibility (`mode === 'preview' ?
 * bypass conditions : honour them`) and `FieldRenderer`'s
 * `repCodeOptionsFor(user, mode ?? 'advisor')`.
 *
 * So the public runtime widens the union at the SHELL BOUNDARY only, and
 * {@link rendererModeFor} narrows it back before it reaches anything below.
 * The leaves keep their two-member union, so `'public'` cannot reach them at
 * all — the compiler enforces the blast radius rather than a reviewer having
 * to re-derive it. Where public genuinely must differ at a leaf (address
 * autocomplete) it travels as its own capability prop, not as a mode.
 */

/** The three audiences the shell serves. */
export type FormShellMode = 'preview' | 'advisor' | 'public';

/**
 * What the leaf renderers need to know, which is only ever two things: is this
 * a reviewer inspecting an unanswered form, or is a real person filling it in.
 */
export type RendererMode = 'preview' | 'advisor';

/**
 * Narrows a shell mode for the field tree below it.
 *
 * A public customer maps to `'advisor'` because in every respect the field
 * tree cares about, that is what they are: conditions are honoured, hidden
 * sections vanish, required fields gate Next, nothing is bypassed. `'preview'`
 * is the odd one out — it is the only mode that shows content the answers say
 * should be hidden, and a customer must never see that.
 */
export function rendererModeFor(mode: FormShellMode): RendererMode {
  return mode === 'preview' ? 'preview' : 'advisor';
}
